import { DropdownOption } from 'components/final-form/dropdownUtils';
import { generateRadioButtonOptions } from 'components/final-form/radioUtils';
import * as React from 'react';
import { Component } from 'react';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { Accordion, Button, Grid, Header, Icon, RadioProps } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import { BillDocumentDto, UpsertMandateTariffDto } from 'ts-types/api.types';
import { BillXmlType, PrintoutConfiguration } from 'ts-types/api.enums';
import Select from 'components/final-form/Select';
import RadioGroup from 'components/final-form/RadioGroup';
import axios, { CancelTokenSource } from 'axios';
import createDecorator from 'final-form-calculate';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import styled from 'styled-components';
import { required } from 'utils/validatorUtils';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';

const StyledHeaderRow = styled(Grid.Row)`
  margin-top: 1.5rem;
`;

const StyledTopBottomGrid = styled(Grid)`
  border-bottom: 1px solid rgba(34, 36, 38, .1);
  border-top: 1px solid rgba(34,36,38,.1);
`;

const StyledBottomGrid = styled(Grid)`
  border-bottom: 1px solid rgba(34, 36, 38, .1);
`;

interface ExpandedUpsertMandateTariffDto extends UpsertMandateTariffDto {
  billPrintoutTypesSelected: Array<DropdownOption>,
  reminderPrintoutTypesSelected: Array<DropdownOption>
}

interface Props extends MessageSourceProps {
  activeIndex?: number,
  activeIndexPath?: number,
  editMandateTariff?: Partial<UpsertMandateTariffDto>,
  billDocuments: Array<BillDocumentDto>,
  onSave: (mandateTariff: Partial<UpsertMandateTariffDto>) => void,
  onCancel: () => void
}

interface State {
  activeIndex?: number,
  activeIndexPath?: number,
  xmlTypes: Array<DropdownOption>,
  billPrintoutConfigurations: Array<RadioProps>,
  reminderPrintoutConfigurations: Array<RadioProps>,
  predefinedPrintoutTypes: Array<DropdownOption>,
  userDefinedBillPrintoutTypes: Array<DropdownOption>,
  userDefinedReminderPrintoutTypes: Array<DropdownOption>,
  cancelTokenSource: CancelTokenSource
}

class UpsertMandateTariffForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage, billDocuments, activeIndex, activeIndexPath} = props;
    const cancelTokenSource = axios.CancelToken.source();

    const xmlTypes: Array<DropdownOption> = Object.values(BillXmlType).map((value: string) => {
      return {
        key: value,
        text: value,
        value: value
      }
    });

    const billPrintoutConfigurations: Array<RadioProps> =
        generateRadioButtonOptions(
            Object.values(PrintoutConfiguration).map(str => "BILL_" + str),
            "billPrintoutConfiguration",
            getMessage,
            "printoutConfiguration",
            Object.values(PrintoutConfiguration));

    const reminderPrintoutConfigurations: Array<RadioProps> =
        generateRadioButtonOptions(
            Object.values(PrintoutConfiguration).map(str => "REMINDER_" + str),
            "reminderPrintoutConfiguration",
            getMessage,
            "printoutConfiguration",
            Object.values(PrintoutConfiguration));

    let predefinedPrintoutTypes = [
      {
        key: "HORIZONTAL",
        text: getMessage("predefinedPrintoutTypes.HORIZONTAL"),
        value: "10"
      },
      {
        key: "VERTICAL_LONG",
        text: getMessage("predefinedPrintoutTypes.VERTICAL_LONG"),
        value: "20"
      },
      {
        key: "VERTICAL_SHORT",
        text: getMessage("predefinedPrintoutTypes.VERTICAL_SHORT"),
        value: "30"
      },
      {
        key: "SPITEXT",
        text: getMessage("predefinedPrintoutTypes.SPITEXT"),
        value: "40"
      }
    ];

    let userDefinedBillPrintoutTypes = Object.values(billDocuments.filter(bd => bd.type === 1))
        .map((bd: BillDocumentDto) => {
          return {
            key: bd.code,
            text: bd.name,
            value: bd.code.toString()
          }
        });

    let userDefinedReminderPrintoutTypes = Object.values(billDocuments.filter(bd => bd.type === 2))
        .map((bd: BillDocumentDto) => {
          return {
            key: bd.code,
            text: bd.name,
            value: bd.code.toString()
          }
        });

    this.state = {
      userDefinedBillPrintoutTypes,
      userDefinedReminderPrintoutTypes,
      xmlTypes,
      billPrintoutConfigurations,
      reminderPrintoutConfigurations,
      predefinedPrintoutTypes,
      cancelTokenSource,
      activeIndex: activeIndex,
      activeIndexPath: activeIndexPath
    };

  }

  billPrintoutTypeDecorator = createDecorator({
    field: "billPrintoutType",
    updates: (billPrintoutType) => {
      const {predefinedPrintoutTypes, userDefinedBillPrintoutTypes} = this.state;
      return {
        billPrintoutTypesSelected: billPrintoutType === PrintoutConfiguration.PREDEFINED ?
            predefinedPrintoutTypes :
            userDefinedBillPrintoutTypes
      }
    }
  });

  reminderPrintoutTypeDecorator = createDecorator({
    field: "reminderPrintoutType",
    updates: (reminderPrintoutType) => {
      const {predefinedPrintoutTypes, userDefinedReminderPrintoutTypes} = this.state;
      return {
        reminderPrintoutTypesSelected: reminderPrintoutType === PrintoutConfiguration.PREDEFINED ?
            predefinedPrintoutTypes :
            userDefinedReminderPrintoutTypes
      }
    }
  });

  render(): React.ReactNode {
    const {editMandateTariff} = this.props;
    const {userDefinedBillPrintoutTypes, userDefinedReminderPrintoutTypes, predefinedPrintoutTypes} = this.state;

    let initialValues: Partial<ExpandedUpsertMandateTariffDto> = {
      ...editMandateTariff
    };

    initialValues.reminderPrintoutType = initialValues.reminderPrintoutType
        ? initialValues.reminderPrintoutType
        : PrintoutConfiguration.PREDEFINED;

    initialValues.billPrintoutType = initialValues.billPrintoutType
        ? initialValues.billPrintoutType
        : PrintoutConfiguration.PREDEFINED;

    editMandateTariff && editMandateTariff.billPrintoutType === PrintoutConfiguration.USERDEFINED ?
        initialValues.billPrintoutTypesSelected = userDefinedBillPrintoutTypes :
        initialValues.billPrintoutTypesSelected = predefinedPrintoutTypes;

    editMandateTariff && editMandateTariff.reminderPrintoutType === PrintoutConfiguration.USERDEFINED ?
        initialValues.reminderPrintoutTypesSelected = userDefinedReminderPrintoutTypes :
        initialValues.reminderPrintoutTypesSelected = predefinedPrintoutTypes;

    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
            mutators={{
              ...arrayMutators
            }}
            decorators={[this.billPrintoutTypeDecorator, this.reminderPrintoutTypeDecorator]}
        />
    );
  }

  handleSubmit = (mandateTariff: Partial<ExpandedUpsertMandateTariffDto>) => {
    const {onSave} = this.props;
    mandateTariff.toBeDeleted = false;

    if (mandateTariff.billPrintoutConf && !mandateTariff.billPrintoutTypesSelected!
        .find(x => x.value === mandateTariff.billPrintoutConf)) {
      mandateTariff.billPrintoutConf = undefined;
    }

    if (mandateTariff.reminderPrintoutConf && !mandateTariff.reminderPrintoutTypesSelected!
        .find(x => x.value === mandateTariff.reminderPrintoutConf)) {
      mandateTariff.reminderPrintoutConf = undefined;
    }

    onSave(mandateTariff);
  };

  renderForm = ({handleSubmit, values}: FormRenderProps): React.ReactNode => {
    const {getMessage, onCancel} = this.props;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <PraxFormScrollableContainer height="645px">
                  {this.renderCodeSegment()}
                  {this.renderActiveTestSegment()}
                  {this.renderBillPrintOutConfigurationSegment(values)}
                  {this.renderReminderPrintoutConfigurationSegment(values)}
                  {this.renderOptionsSegment()}
                  {this.renderPathSegment()}
                </PraxFormScrollableContainer>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary
                      disabled={false}>{getMessage("button.save")}</Button>
              <Button compact type="button"
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderCodeSegment = () => {
    const {getMessage} = this.props;
    const {xmlTypes} = this.state;

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("mandate.billConfigurations.code")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.billConfigurations.code")}
            </Grid.Column>
            <Grid.Column width={5} verticalAlign="middle">
              <Field
                  fluid
                  name="code"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.billConfigurations.xmlType")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="xmltype"
                  component={Select}
                  fluid
                  search
                  clearable
                  options={xmlTypes}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderActiveTestSegment = () => {
    const {getMessage} = this.props;

    return (
        <PraxFormGrid>
          <StyledHeaderRow>
            <Grid.Column>
              <Header>{getMessage("mandate.billConfigurations.activeTest")}</Header>
            </Grid.Column>
          </StyledHeaderRow>
          <Grid.Row>
            <Grid.Column width={5}>
            </Grid.Column>
            <Grid.Column width={11}>
              <Field
                  name="activeXmlInvoice"
                  component={CheckBox}
                  label={`${getMessage("mandate.billConfigurations.activeXmlInvoice")}`}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}>
            </Grid.Column>
            <Grid.Column width={11}>
              <Field
                  name="testXmlInvoiceMode"
                  component={CheckBox}
                  label={`${getMessage("mandate.billConfigurations.testXmlInvoiceMode")}`}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderBillPrintOutConfigurationSegment = (values: any) => {
    const {getMessage} = this.props;
    const {billPrintoutConfigurations} = this.state;

    return (
        <PraxFormGrid>
          <StyledHeaderRow>
            <Grid.Column>
              <Header>{getMessage("mandate.billConfigurations.billPrintoutConf")}</Header>
            </Grid.Column>
          </StyledHeaderRow>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.billConfigurations.billPrintoutType")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="billPrintoutType"
                  component={RadioGroup}
                  radioDefinitions={billPrintoutConfigurations}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.billConfigurations.billPrintoutConf")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="billPrintoutConf"
                  component={Select}
                  fluid
                  search
                  clearable
                  options={values.billPrintoutTypesSelected}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="disableTarmedPrintout"
                  component={CheckBox}
                  label={`${getMessage("mandate.billConfigurations.disableTarmedPrintout")}`}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="isVerticalBillEsr"
                  component={CheckBox}
                  label={`${getMessage("mandate.billConfigurations.isVerticalBillEsr")}`}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderReminderPrintoutConfigurationSegment = (values: any) => {
    const {getMessage} = this.props;
    const {reminderPrintoutConfigurations} = this.state;

    return (
        <PraxFormGrid>
          <StyledHeaderRow>
            <Grid.Column>
              <Header>{getMessage("mandate.billConfigurations.reminderPrintoutConf")}</Header>
            </Grid.Column>
          </StyledHeaderRow>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.billConfigurations.reminderPrintoutType")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="reminderPrintoutType"
                  component={RadioGroup}
                  radioDefinitions={reminderPrintoutConfigurations}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.billConfigurations.reminderPrintoutConf")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="reminderPrintoutConf"
                  component={Select}
                  fluid
                  search
                  clearable
                  options={values.reminderPrintoutTypesSelected}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  openOptionsSegment = (e: any, titleProps: any) => {
    const {index} = titleProps;
    const {activeIndex} = this.state;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({activeIndex: newIndex})
  };

  renderOptionsSegment = () => {
    const {getMessage} = this.props;
    const {activeIndex} = this.state;

    return (
        <StyledTopBottomGrid>
          <Accordion fluid>
            <Accordion.Title active={activeIndex === 0} index={0} onClick={this.openOptionsSegment}>
              <h4><Icon name='dropdown'/>{getMessage("mandate.billConfigurations.options")}</h4>
            </Accordion.Title>
            <Accordion.Content active={activeIndex === 0} style={{paddingBottom: 20}}>

              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.intermediateType")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="intermediateType"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.intermediateEanParty")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="intermediateEanParty"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.trustCenterCode")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="trustCenterCode"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.sender")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="sender"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.recipient")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="recipient"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}></Grid.Column>
                  <Grid.Column style={{width: "201px"}} verticalAlign="middle">
                    <Field
                        name="xmlContentEncrypted"
                        component={CheckBox}
                        label={`${getMessage("mandate.billConfigurations.xmlContentEncrypted")}`}
                    />
                  </Grid.Column>
                  <Grid.Column style={{width: "177px"}} verticalAlign="middle">
                    <Field
                        name="automaticPayment"
                        component={CheckBox}
                        label={`${getMessage("mandate.billConfigurations.automaticPayment")}`}
                    />
                  </Grid.Column>
                  <Grid.Column style={{width: "187px"}} verticalAlign="middle">
                    <Field
                        name="activeXmlReminder"
                        component={CheckBox}
                        label={`${getMessage("mandate.billConfigurations.activeXmlReminder")}`}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Accordion.Content>
          </Accordion>
        </StyledTopBottomGrid>
    );
  };

  openPathSegment = (e: any, titleProps: any) => {
    const {index} = titleProps;
    const {activeIndexPath} = this.state;
    const newIndex = activeIndexPath === index ? -1 : index;

    this.setState({activeIndexPath: newIndex})
  };

  renderPathSegment = () => {
    const {getMessage} = this.props;
    const {activeIndexPath} = this.state;

    return (
        <StyledBottomGrid>
          <Accordion fluid>
            <Accordion.Title active={activeIndexPath === 0} index={0} onClick={this.openPathSegment}>
              <h4><Icon name='dropdown'/>{getMessage("mandate.billConfigurations.path")}</h4>
            </Accordion.Title>
            <Accordion.Content active={activeIndexPath === 0} style={{paddingBottom: 20}}>

              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.invoicePath")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="invoicePath"
                        component={Input}
                        // validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.temporaryInvoicePath")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="tmpInvoicePath"
                        component={Input}
                        // validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.invoiceSentPath")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="sentInvoicePath"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.responsePath")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="responsePath"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.viewedResponsePath")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="responseSavedPath"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.docStatusPath")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="docStatusPath"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("mandate.billConfigurations.docStatusArchivePath")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        fluid
                        name="docStatusArchivePath"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Accordion.Content>
          </Accordion>
        </StyledBottomGrid>
    )
  }

}

export default withMessages(UpsertMandateTariffForm);
